import { Fragment, type MouseEventHandler } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { BreadcrumbButton, type BreadcrumbButtonState } from '../BreadcrumbButton/BreadcrumbButton';
import styles from './Breadcrumbs.module.css';

/**
 * Figma: `Breadcrumbs` (200:2544) — a horizontal trail of `_Breadcrumb button
 * base` instances separated by `chevron right`, 4px apart, vertically centred.
 *
 * The set carries one variant property, `State`, with a single option
 * (`Default`) — the `Divider`, `Type` and `Breakpoint` axes were removed on
 * 2026-08-11. A one-option axis has nothing to switch between, so it does not
 * become a prop. `state` below is the repo's usual forced-state escape hatch for
 * documentation, not that axis.
 *
 * The trail itself is data, not structure: Figma hard-codes two visible crumbs
 * with two more hidden on the canvas as spares, which is not something an
 * instance can vary. `items` drives it here, the same way `Sidebar` takes
 * `sections`.
 */
export type BreadcrumbItem = {
  label: string;
  href?: string;
  onClick?: MouseEventHandler<HTMLElement>;
};

export type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  /**
   * Accessible name for the landmark. Not in Figma — a page can hold more than
   * one `<nav>`, so this one has to say which it is.
   */
  label?: string;
  /** Force every crumb's state for documentation and visual regression only. */
  state?: BreadcrumbButtonState;
  className?: string;
};

export function Breadcrumbs({ items, label = 'Breadcrumb', state, className }: BreadcrumbsProps) {
  return (
    <nav className={[styles.nav, className].filter(Boolean).join(' ')} aria-label={label}>
      {/*
       * An ordered list, because the order is the meaning — this is the path
       * from the root to where you are. Not in Figma, which has only a row of
       * frames.
       */}
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className={styles.item}>
                <BreadcrumbButton
                  current={isLast}
                  href={item.href}
                  onClick={item.onClick}
                  state={isLast ? undefined : state}
                >
                  {item.label}
                </BreadcrumbButton>
              </li>

              {/*
               * Figma: `chevron right`, 12x12, icon/secondary — which is exactly
               * what the `Icon` atom already defaults to, so nothing overrides
               * it. Decorative: the list order already conveys the hierarchy, so
               * announcing a chevron between every crumb is noise.
               */}
              {!isLast && (
                <li className={styles.separator} aria-hidden="true">
                  <Icon name="chevronRight" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
